import { useEffect } from 'react';
import styles from './TopNavigation.module.scss';
import {
	Text, Link, OfloadIcon, Spacer, Loader, ShipperCostCenterSelector, FloatingMenu
} from '@components';
import AppsIcon from '@mui/icons-material/Apps';
import SearchIcon from '@mui/icons-material/Search';
import MenuIcon from '@mui/icons-material/Menu';
import ArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import MonetizationOnOutlinedIcon from '@mui/icons-material/MonetizationOnOutlined';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import TransitEnterexitIcon from '@mui/icons-material/TransitEnterexit';
import LockResetIcon from '@mui/icons-material/LockReset';
// import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import SecurityIcon from '@mui/icons-material/Security';
import LogoutIcon from '@mui/icons-material/Logout';
import CloseIcon from '@mui/icons-material/Close';
import InventoryOutlinedIcon from '@mui/icons-material/InventoryOutlined';
import { ShipperIcon, TransportIcon } from '@img/icons';
import { useLocation, useNavigate } from 'react-router-dom';
import { SPACER_DIRECTION, TEXT_WEIGHT, navigator } from '@types';
import { clearPendoIdentify, clearCookies } from '@utils';
import { useLogout } from '@api';
import { useRecoilValue, useRecoilState, useSetRecoilState } from 'recoil';
import {
	UserName,
	IsAdmin,
	SideNavOpen,
	UserMenuOpen,
	JWT,
	SID,
	CID,
	// CID
	CarrierModuleOpen,
	EfficiencyModuleOpen,
	GlobalSearchOpen,
} from '@state';

export const TopNavigation = () => {
	const setJWT = useSetRecoilState(JWT);
	const setCarrierModuleOpen = useSetRecoilState(CarrierModuleOpen);
	const setEfficiencyModuleOpen = useSetRecoilState(EfficiencyModuleOpen);
	const setGlobalSearchOpen = useSetRecoilState(GlobalSearchOpen);
	const userName = useRecoilValue(UserName);
	const isAdmin = useRecoilValue(IsAdmin);
	const sID = useRecoilValue(SID);
	const cID = useRecoilValue(CID);
	const [sideNavOpen, setSideNavOpen] = useRecoilState(SideNavOpen);
	const [userMenuOpen, setUserMenuOpen] = useRecoilState(UserMenuOpen);
	const location = useLocation();
	const navigate = useNavigate();
	const {
		isLoadingLogout,
		logoutData,
		logoutError,
		callLogoutAPI
	} = useLogout();

	const triggerLogout = () => {
		callLogoutAPI();
	}
	const goToURL = (url: string, newWindow: boolean = false) => {
		// navigate('/forgot'); // --> FOR SPA!!
		if (newWindow) {
			window.open(url);
		} else {
			window.location.href = url;
		}
	}
	const isActive = (label:string) => {
		const parts = location.pathname.split('/');
		return parts[1].toLocaleLowerCase() === label.toLowerCase();
	}
	const navItems:navigator[] = [
		{
			label: 'Inbox',
			icon: (color:string) => <TransitEnterexitIcon className={styles.icons} style={{ transform: 'rotate(270deg)', color: color }} />,
			link: 'inbox',
			domain: 'spa'
		},
		{
			label: 'Shipper',
			icon: (color:string) => <ShipperIcon width={22} height={22} color={color} />,
			link: (sID === null) ? 'shipper/dashboard' : `shipper/dashboard?sid=${sID}`,
			domain: 'spa'
		},
		{
			label: 'Carrier',
			icon: (color:string) => <LocalShippingOutlinedIcon className={styles.icons} style={{ color: color }} />,
			link: (cID === null) ? 'carrier/dashboard' : `carrier/dashboard?cid=${cID}`, // --> SPA
			domain: 'spa'
		},
		{
			label: 'Transport',
			icon: (color:string) => <TransportIcon width={22} height={22} color={color} />,
			link: 'transport/all-loads',
			domain: 'spa'
		},
		{
			label: 'Finance',
			icon: (color:string) => <MonetizationOnOutlinedIcon className={styles.icons} style={{ color: color }} />,
			link: 'finance/shipper_billing',
			domain: 'spa'
		}
	];

	const runLogoutCleanup = async () => {
		// @ts-ignore
		if (window.pendo) {
			clearPendoIdentify();
		}
		await sessionStorage.clear();
		await localStorage.removeItem('jwt');
		await localStorage.removeItem('refreshToken');
		await clearCookies();
		setUserMenuOpen(false);
		setJWT(null);
		navigate('/');
	}

	useEffect(() => {
		if (logoutData) {
			runLogoutCleanup();
		}
	}, [logoutData]);

	useEffect(() => {
		if (logoutError) {
			// Force logout jwt if any error
			runLogoutCleanup();
		}
	}, [logoutError]);

	return (
		<div data-testid={'@Ofload-TopNavigation'} className={`${styles.container} ${sideNavOpen ? styles.menuPadding : ''}`}>
			{!sideNavOpen && <button data-testid={'@Ofload-TopNav-ToggleBtn'} className={styles.menuButton} onClick={() => { setSideNavOpen(true) }} aria-label={'Toggle Side Nav'}>
				<MenuIcon />
			</button>}
			{isAdmin && <nav className={styles.menuItems}>
				{navItems.map((item, i) => {
					const color = isActive(item.label) ? 'var(--brand-orange-default)' : 'var(--neutral-07)';
					return <Link domain={item.domain || 'spa'} to={item.link} key={i} className={`${styles.menuItem} ${isActive(item.label) ? styles.activeItem : ''}`}>{item.icon(color)}<Text text={item.label} weight={isActive(item.label) ? TEXT_WEIGHT.SEMI_BOLD : TEXT_WEIGHT.REGULAR} color={color} /></Link>
				})}
			</nav>}
			{!isAdmin && isActive('Shipper') && <ShipperCostCenterSelector />}
			{isAdmin && (
				<div className={styles.iconButtons}>
					<button className={styles.iconBtn} aria-label={'Global Search'} onClick={() => setGlobalSearchOpen(true)}>
						<SearchIcon />
					</button>
					<button className={styles.iconBtn} aria-label={'Configuration'} onClick={() => navigate('/configuration/dashboard')}>
						<SettingsOutlinedIcon />
					</button>
					<FloatingMenu>
						<button className={styles.iconBtn} aria-label={'EOS Tools'}>
							<AppsIcon />
						</button>
						<FloatingMenu.Menu menuPos={'right'}>
							<div className={styles.appsMenuContent}>
								<button
									className={styles.appsMenuItem}
									onClick={() => setCarrierModuleOpen(true)}
								>
									<LocalShippingOutlinedIcon /> Carrier Recommendation
								</button>
								<button
									className={styles.appsMenuItem}
									onClick={() => setEfficiencyModuleOpen(true)}
								>
									<InventoryOutlinedIcon /> Efficiency Recommendation
								</button>
							</div>
						</FloatingMenu.Menu>
					</FloatingMenu>
				</div>
			)}
			<div data-testid={'@Ofload-TopNav-UserMenu'} className={styles.user} onClick={() => { setUserMenuOpen(!userMenuOpen) }}>
				<Text text={userName ?? 'Ofload'} />
				<ArrowDownIcon />
			</div>
			<div className={`${styles.userMenu2} ${userMenuOpen ? styles.openMenu : ''}`}>
				<button className={styles.closeBtn} onClick={() => { setUserMenuOpen(false) }} aria-label={'Toggle User Menu'}><CloseIcon style={{ color: 'var(--brand-orange-default)' }} /></button>
				<div className={styles.userTile}>
					<div className={styles.logo}>
						<OfloadIcon color={'var(--brand-navy-default)'} accent={'var(--brand-orange-default)'} size={50} />
					</div>
					<Spacer size={10} />
					<Text type={'h5'} text={'Ofload'} />
				</div>
				<Spacer size={40} />
				<button onClick={() => goToURL('/reset')} aria-label={'Reset Password'}>
					<Text>
						<LockResetIcon /> Reset Password
					</Text>
				</button>
				{/* <button onClick={() => {}} aria-label={'Help'}>
					<Text>
						<HelpOutlineIcon /> Help
					</Text>
				</button> */}
				<button onClick={() => goToURL('https://www.ofload.com.au/privacy-policy?_gl=1*1cbde4n*_ga*Mzg1NDU1ODI1LjE3MjI5MDU3NTQ.*_ga_5GPTS2PNJT*MTcyMjkxMTUwOC4yLjEuMTcyMjkxMTc2NC41NC4wLjA.', true)} aria-label={'Privacy Policy'}>
					<Text>
						<SecurityIcon /> Privacy Policy
					</Text>
				</button>
				<button onClick={triggerLogout} aria-label={'Logout'}>
					<Text>
						{!isLoadingLogout && <LogoutIcon />}
						{isLoadingLogout && <>
							<Spacer size={2} dir={SPACER_DIRECTION.VERTICAL} />
							<Loader size={20} thickness={3} />
							<Spacer size={12} dir={SPACER_DIRECTION.VERTICAL} />
						</>}
						Logout
					</Text>
				</button>
			</div>
		</div>
	);
}
